import axios from "axios";
import { config } from "./runtimeConfig";

/**
 * Same auth-bridge pattern as web/src/services/apiClient.js. X-Session-Type
 * is "admin" rather than "web" -- GAP: api/security/session_manager.py's
 * _session_timeout() only branches on "mobile" vs everything-else (2h), so
 * today an "admin" session type still gets the 2-hour web timeout
 * server-side; the 15-minute session enforced in this app (see
 * hooks/useAdminSession.js) is a client-side safeguard only until
 * _session_timeout() gets an "admin" branch. Sent anyway so the signal is
 * already correct the moment that's added.
 */
let authState = {
  getAccessToken: async () => null,
  deviceFingerprint: "",
  stepUpMfa: () => {},
  onUnauthorized: () => {},
  onForbidden: () => {},
  onJitRequired: () => {},
};

export function setApiAuthState(next) {
  authState = { ...authState, ...next };
}

export const apiClient = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 20000,
});

apiClient.interceptors.request.use(async (req) => {
  const token = await authState.getAccessToken();
  if (token) req.headers.Authorization = `Bearer ${token}`;
  req.headers["X-Device-Fingerprint"] = authState.deviceFingerprint;
  req.headers["X-Session-Type"] = "admin";
  req.headers["X-Stay-Logged-In"] = "false"; // admin sessions never persist across browser restarts
  return req;
});

apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.error;

    if (status === 401 && code === "mfa_required") {
      authState.stepUpMfa();
    } else if (status === 401) {
      authState.onUnauthorized();
    } else if (status === 403 && code === "jit_access_required") {
      // Caller is a valid B2C admin but has no active, second-admin-approved
      // JITAccessRequests grant for this route (see services/auth.py's
      // secure_endpoint require_jit). Distinct from the generic "forbidden"
      // (not an admin at all) -- route to the request-access flow instead
      // of the "no admin access" page.
      authState.onJitRequired();
    } else if (status === 403) {
      authState.onForbidden();
    }

    error.apiMessage = error.response?.data?.message || error.message;
    error.apiErrorCode = code;
    return Promise.reject(error);
  }
);
